import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Radar, Plus, Trash2, Bell, MapPin, FileText, MousePointerClick, LayoutDashboard, BarChart3, Zap, Clock, SlidersHorizontal, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const features = [
  { icon: Radar, title: "Real-Time Job Monitoring", desc: "Continuous scanning of 300+ major job boards, 150+ niche platforms, and direct company career pages" },
  { icon: Bell, title: "Instant Multi-Channel Alerts", desc: "Real-time phone and email notifications the moment a matching job is posted" },
  { icon: LayoutDashboard, title: "Custom Company Tracking", desc: "Add any company to your watch list and get alerts when they post matching roles" },
  { icon: MapPin, title: "Location Preference Controls", desc: "Filter by city, state, zip code, commute radius, or maximum commute time" },
  { icon: FileText, title: "Auto-Generated Resume Package", desc: "Automatically generates tailored resume, cover letter, solution asset, and outreach letter" },
  { icon: MousePointerClick, title: "One-Click Apply", desc: "Apply directly from the dashboard with tailored documents already attached" },
  { icon: BarChart3, title: "Job Search Dashboard", desc: "Centralized view of every opportunity, application, document, and pipeline stage" },
  { icon: SlidersHorizontal, title: "Application Status Tracking", desc: "Track every application from submitted to offer with automated follow-up reminders" },
  { icon: Star, title: "Opportunity Scoring", desc: "Every job auto-scored based on alignment with your background and preferences" },
  { icon: Zap, title: "JobIntel 360 Integration", desc: "Send any job to JobIntel 360 with one click for a full intelligence report" },
  { icon: Clock, title: "First Mover Advantage", desc: "Posting age timestamps and competition level indicators so you know when to move fast" },
];

export default function JobSearchTrackingHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    fetchReports();
  }, [user]);

  const fetchReports = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('job_search_tracking_reports' as any)
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching job search tracking reports:', error);
    } else {
      setReports(data || []);
    }
    setLoading(false);
  };

  const deleteReport = async (id: string) => {
    const { error } = await supabase.from('job_search_tracking_reports' as any).delete().eq('id', id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete report", variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Report removed" });
      setReports(prev => prev.filter(r => (r as any).id !== id));
    }
  };

  return (
    <AppLayout currentPage="job-search-tracking-hub">
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/jobintel-360-reports')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Advanced Job Search & Tracking Hub</h1>
              <p className="text-muted-foreground">Complete, centralized control of your entire job search</p>
            </div>
          </div>
        </div>

        {/* Hero Description */}
        <Card className="mb-8 border-2 border-sky-200 bg-gradient-to-br from-sky-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Radar className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold mb-2">Your Entire Job Search — Automated, Centralized, and Always On</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The Advanced Job Search and Tracking Hub gives you complete, centralized control of your entire job search from a single dashboard without ever needing to sign up for LinkedIn, Indeed, or any other job platform. You set your criteria once and the system does everything else — continuously, automatically, and in real time. It monitors more than 300 major job boards, 150+ niche specialty sites, and the career pages of the top 100 companies in your target field simultaneously. The moment a matching role is posted anywhere, you receive an instant alert so you are among the very first candidates to apply.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <h3 className="text-lg font-bold mb-4">What's Included</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <Card key={f.title} className="border border-sky-100 hover:border-sky-300 transition-colors">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{f.title}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Saved Reports Section */}
        <div className="border-t pt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Your Saved Hub Sessions</h3>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>
          ) : reports.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <Radar className="w-12 h-12 text-sky-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No tracking sessions yet</h3>
                <p className="text-muted-foreground mb-4">Your saved job search tracking sessions will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {reports.map((r: any) => (
                <Card
                  key={r.id}
                  className="cursor-pointer hover:shadow-md transition-shadow border border-sky-200 hover:border-sky-400"
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                        <Radar className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">
                          {r.session_name || r.job_title || 'Job Search Session'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(r.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={r.status === 'completed' ? 'default' : r.status === 'failed' ? 'destructive' : 'secondary'}>
                        {r.status || 'active'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
